import { useContext, useEffect, useState } from 'react';
import { useHref, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { FileDownIcon, AudioLines } from 'lucide-react';
import { IssueContext } from '../Issue.context';
import { ReplicaContext } from '../replica/Replica.context';
import { Icon, IconType, isValidIconType } from '../../../components/icon/Icon';

import config from '../../../config';

import styles from './MenuButton.module.css';
import { Article } from '../../../constants';
import { AppContext } from '../../../App.context';
import { TrackingContext } from '../../../tracking/Tracking.context';
import { LinkItem } from '../../../config.type';

const MIN_FONT_SIZE = 30,
	MAX_FONT_SIZE = 100,
	FONT_STEP = 10;

export class Button {
	static ShareArticle = ShareArticleButton;
	static Replica = ReplicaButton;
	static SignOut = SignOutButton;
	static Links = Links;
	static Library = Library;
	static Responsive = ResponsiveButton;
	static SinglePage = SinglePageButton;
	static DoublePage = DoublePageButton;
	static Favorite = FavoriteButton;
	static Font = FontSizeButton;
	static WebsiteButton = WebsiteButton;
	static Legal = Legal;
	static About = AboutButton;
	static Language = Language;
	static Audio = Audio;
	static ExternalUrl = ExternalUrl;
}

function Links({ links }: LinkArgs) {
	if (!links || !links.length) {
		return null;
	}
	return (
		<section className={styles['links']}>
			{links
				.filter(l => l.title && l.url)
				.map((l, i) => {
					const { url, title, icon } = l;
					return (
						<a key={i} href={url} target="_blank" rel="noreferrer">
							<h4>{title}</h4>
							<div>
								{isValidIconType(icon) ? (
									<Icon name={icon as IconType} />
								) : null}
							</div>
						</a>
					);
				})}
		</section>
	);
}

interface LinkArgs {
	links: Array<LinkItem> | undefined;
}

function Library() {
	const navigate = useNavigate();
	return (
		<button
			type={'button'}
			onClick={() => {
				navigate('/');
			}}
		>
			<span>Library</span>
			<span>
				<Icon name={'home'} color={'#000'} />
			</span>
		</button>
	);
}

function ExternalUrl({ onClick }: ExternalUrlProps) {
	return (
		<button type={'button'} onClick={onClick}>
			<span>Download Issue</span>
			<span>
				<FileDownIcon size={24} />
			</span>
		</button>
	);
}

interface ExternalUrlProps {
	onClick: () => void;
}

function SignOutButton({ onClick }: SignOutProps) {
	const basename = useHref('/logout');
	return (
		<button
			type={'button'}
			onClick={() => {
				onClick(basename);
			}}
		>
			<span>Log Out</span>
			<span>
				<Icon name={'logout'} />
			</span>
		</button>
	);
}

interface SignOutProps {
	onClick: (url?: string) => void;
}

function Legal() {
	const components = [];
	const { title } = config.build;
	const { legal } = config.app;
	components.push(
		<div className="optanon-toggle-display">
			© Copyright {title}, Inc. All material that appears on this app is
			protected by United States copyright law and may not be reproduced,
			distributed, transmitted, cached, or otherwise used, except with
			prior written permission of {title}, Inc. 2024. All Rights Reserved.
		</div>,
	);
	if (legal?.tou) {
		components.push(
			<a href={legal.tou.url} target="_blank" rel="noreferrer">
				{legal.tou.label}
			</a>,
		);
	}
	if (legal?.privacy) {
		components.push(
			<a href={legal.privacy.url} target="_blank" rel="noreferrer">
				{legal.privacy.label}
			</a>,
		);
	}
	components.push(<div className="optanon-toggle-display">Do Not Sell</div>);
	if (legal?.cookie) {
		components.push(
			<a href={legal.cookie.url} target="_blank" rel="noreferrer">
				{legal.cookie.label}
			</a>,
		);
	}
	return (
		<div className={styles['legal']}>
			<div>{components}</div>
		</div>
	);
}

function FontSizeButton({
	fontSize,
	setFontSize,
}: {
	fontSize: any;
	setFontSize: any;
}) {
	const animations = {
		hover: {
			scale: 1.1,
		},
		tap: {
			scale: 0.8,
		},
	};
	return (
		<div className={styles['font-button']}>
			<DecreaseFontButton
				fontSize={fontSize}
				setFontSize={setFontSize}
				animations={animations}
			/>
			<span>Text Size</span>
			<IncreaseFontButton
				fontSize={fontSize}
				setFontSize={setFontSize}
				animations={animations}
			/>
		</div>
	);
}

function DecreaseFontButton({ animations, fontSize, setFontSize }: FontButton) {
	const title = 'Decrease font size';

	const onClick = () => {
		const newFontSize = fontSize - FONT_STEP;
		setFontSize(newFontSize <= MIN_FONT_SIZE ? MIN_FONT_SIZE : newFontSize);
	};

	const isDisabled = fontSize === MIN_FONT_SIZE;

	if (isDisabled) {
		return (
			<motion.button
				title={title}
				type={'button'}
				style={{ cursor: 'not-allowed' }}
				disabled
			>
				<Icon name={'font-decrease'} color={'#c3c3c3'} />
			</motion.button>
		);
	}

	return (
		<motion.button
			title={title}
			whileHover={animations.hover}
			whileTap={animations.tap}
			type={'button'}
			style={{ cursor: 'pointer' }}
			onClick={onClick}
		>
			<Icon name={'font-decrease'} color={'#000'} />
		</motion.button>
	);
}

function IncreaseFontButton({ animations, fontSize, setFontSize }: FontButton) {
	const title = 'Increase font size';

	const onClick = () => {
		const newFontSize = fontSize + FONT_STEP;
		setFontSize(newFontSize >= MAX_FONT_SIZE ? MAX_FONT_SIZE : newFontSize);
	};

	const isDisabled = fontSize === MAX_FONT_SIZE;

	if (isDisabled) {
		return (
			<motion.button
				title={title}
				type={'button'}
				style={{ cursor: 'not-allowed' }}
				disabled
			>
				<Icon name={'font-increase'} color={'#c3c3c3'} />
			</motion.button>
		);
	}

	return (
		<motion.button
			title={title}
			whileHover={animations.hover}
			whileTap={animations.tap}
			type={'button'}
			style={{ cursor: 'pointer' }}
			onClick={onClick}
		>
			<Icon name={'font-increase'} color={'#000'} />
		</motion.button>
	);
}

interface FontButton {
	fontSize: any;
	setFontSize: any;
	animations: {
		hover: {
			scale: number;
		};
		tap: {
			scale: number;
		};
	};
}

function WebsiteButton(args: ExternalUrlButtonProps) {
	const { url, title } = args;
	if (!url || !title) {
		return null;
	}

	return (
		<a href={url} target="_blank" rel="noreferrer">
			<span>{title}</span>
			<span>
				<Icon name={'globe'} color={'#000'} />
			</span>
		</a>
	);
}

export function DownloadButton(args: ExternalUrlButtonProps) {
	const url: string = args.url || '';
	const [isDownloading, setIsDownloading] = useState(false);
	const onClick = () => {
		const fileName: string = url.split('/').pop() || '';
		setIsDownloading(true);
		fetch(url)
			.then(res => res.blob())
			.then(blob => {
				console.log(`I successfuly download the file: `, {
					fileName,
				});
				saveBlob(blob, fileName);
			})
			.catch(alert)
			.finally(() => {
				setIsDownloading(false);
			});
	};
	if (isDownloading) {
		return (
			<button type={'button'} title={'Downloading'} disabled>
				<span>Downloading</span>
				<span>
					<Icon name={'download'} color={'#000'} />
				</span>
			</button>
		);
	}

	return (
		<button type={'button'} title={'Download'} onClick={onClick}>
			<span>Download</span>
			<span>
				<Icon name={'download'} color={'#000'} />
			</span>
		</button>
	);
}

interface ExternalUrlButtonProps {
	url?: string;
	title?: string;
}

function ShareArticleButton({
	article,
	language,
	onShare,
}: ShareArticleButtonProps) {
	const title: string = article.name[language] || undefined;
	const url: string = window.location.href;
	const message = 'URL copy to the clipboard';
	const onClick = () => {
		if (navigator.share) {
			navigator
				.share({
					title,
					url,
				})
				.then(() => {
					onShare(article);
					console.log('Successully share url:', {
						title,
						url,
					});
				})
				.catch(console.error);
			return;
		}

		if (navigator.clipboard) {
			navigator.clipboard
				.writeText(url)
				.then(
					() => {
						onShare(article, 'clipboard');
						alert(message);
					},
					() => {
						alert('Fail copy the url');
					},
				)
				.catch(console.error);
			return;
		}

		const inputElement = document.createElement('input');
		document.body.appendChild(inputElement);
		inputElement.value = url;
		inputElement.select();
		document.execCommand('copy');
		document.body.removeChild(inputElement);
		onShare(article, 'clipboard');
		alert(message);
	};
	return (
		<button type={'button'} title={'Share'} onClick={onClick}>
			<span>Share</span>
			<span>
				<Icon name={'share'} color={'#000'} />
			</span>
		</button>
	);
}

interface ShareArticleButtonProps {
	article: any;
	language: string;
	onShare: (article: any, method?: 'os' | 'clipboard') => void;
}

function ReplicaButton(props: ReplicaButtonProps) {
	if (config.issue.contentType === 'responsive') {
		return null;
	}
	return (
		<button type={'button'} title={'Magazine'} onClick={props.onClick}>
			<span>Switch to Magazine View</span>
			<span>
				<Icon name={'replica-mode'} color={'#000'} />
			</span>
		</button>
	);
}

interface ReplicaButtonProps {
	onClick: () => void;
}

function FavoriteButton({
	article,
	favorites,
	setFavorites,
}: FavoriteButtonProps) {
	const { currentArticle, issue, language } = useContext(IssueContext);
	const { sendEvent } = useContext(TrackingContext);
	const [isSelected, setIsSelected] = useState(false);
	useEffect(() => {
		if (!article) {
			return;
		}
		if (!favorites) {
			return;
		}
		const { id } = article;
		setIsSelected(favorites.has(parseInt(`${id}`, 10)));
	}, [article, favorites]);

	if (!config.app.favorite || article?.role === 'advert') {
		return null;
	}

	const onClick = () => {
		if (!article) {
			return;
		}
		const id = parseInt(`${article.id}`, 10);
		const clonedSet = new Set(favorites);
		if (isSelected) {
			sendEvent({
				type: 'BTN_CLICK_UNFAVORITE_ARTICLE',
				data: {
					lang: language,
					issue: issue
						? {
								id: parseInt(`${issue.id}`, 10),
								title: issue.name,
								slug: issue.slug,
						  }
						: undefined,
					article: currentArticle
						? {
								id: parseInt(`${currentArticle.id}`, 10),
								ArticleID: parseInt(
									`${currentArticle.ArticleID}`,
									10,
								),
								slug: currentArticle.slug,
								title: currentArticle.name[language],
						  }
						: undefined,
				},
			});
			if (clonedSet.has(id)) {
				clonedSet.delete(id);
			}
		} else {
			sendEvent({
				type: 'BTN_CLICK_FAVORITE_ARTICLE',
				data: {
					lang: language,
					issue: issue
						? {
								id: parseInt(`${issue.id}`, 10),
								title: issue.name,
								slug: issue.slug,
						  }
						: undefined,
					article: currentArticle
						? {
								id: parseInt(`${currentArticle.id}`, 10),
								ArticleID: parseInt(
									`${currentArticle.ArticleID}`,
									10,
								),
								slug: currentArticle.slug,
								title: currentArticle.name[language],
						  }
						: undefined,
				},
			});
			clonedSet.add(id);
		}
		setFavorites(clonedSet);
	};
	if (!article) {
		return null;
	}
	return (
		<button type={'button'} title={'Favorite'} onClick={onClick}>
			<span>Favorite</span>
			<span>
				<Icon
					name={isSelected ? 'bookmark-selected' : 'bookmark'}
					color={'#000'}
				/>
			</span>
		</button>
	);
}

interface FavoriteButtonProps {
	article: Article | null;
	favorites: Set<number> | null;
	setFavorites: (value: Set<number>) => void;
}

function ResponsiveButton() {
	const { setMode, currentArticle, issue, language } = useContext(
		IssueContext,
	);
	const { sendEvent } = useContext(TrackingContext);
	if (config.issue.contentType === 'replica') {
		return null;
	}
	const onClick = () => {
		setMode('responsive');
		sendEvent({
			type: 'BTN_CLICK_RESPONSIVE',
			location: window.location.pathname,
			data: {
				lang: language,
				issue: {
					id: parseInt(`${issue.id}`, 10),
					title: issue.name,
					slug: issue.slug,
				},
				article: {
					id: parseInt(`${currentArticle.id}`, 10),
					ArticleID: parseInt(`${currentArticle.ArticleID}`, 10),
					slug: currentArticle.slug,
					title: currentArticle.name[language],
				},
			},
		});
	};
	return (
		<button type={'button'} title={'Mobile'} onClick={onClick}>
			<span>Switch to Digital View</span>
			<span>
				<Icon name={'responsive-mode'} color={'#000'} />
			</span>
		</button>
	);
}

function AboutButton() {
	const { setShowDialog } = useContext(AppContext);

	const onClick = () => {
		setShowDialog(true);
	};
	return (
		<button type={'button'} title={'Info'} onClick={onClick}>
			<span>About</span>
			<span>
				<Icon name={'info'} />
			</span>
		</button>
	);
}

function Language({ language, options, onSelectLanguage }: LanguageProps) {
	const { sendEvent } = useContext(TrackingContext);
	const [value, setValue] = useState(language);
	if (!options.length || options.length < 2) {
		return null;
	}

	useEffect(() => {
		if (!language) {
			return;
		}

		sendEvent({
			type: 'LANGUAGE_CHANGE',
			data: {
				lang: language,
			},
		});
	}, [language]);

	const onChange = (e: any) => {
		const { value } = e.target;
		setValue(value);

		const selectedOption = options.filter(o => {
			if (o.value === value) {
				return true;
			}
			return false;
		});
		if (!selectedOption.length) {
			return;
		}
		onSelectLanguage(selectedOption[0]);
	};

	return (
		<div className={styles['language']}>
			<span>Language</span>
			<span>
				<select
					value={value}
					title={'Select language'}
					onChange={onChange}
				>
					{options.map((o, i) => {
						const { name, value } = o;
						if (o.disabled) {
							return (
								<option key={i} value={value} disabled>
									{name}
								</option>
							);
						}

						return (
							<option key={i} value={value}>
								{name}
							</option>
						);
					})}
				</select>
			</span>
		</div>
	);
}

interface LanguageProps {
	language: string;
	options: Array<LanguageOption>;
	onSelectLanguage: (e: LanguageOption) => void;
}

export interface LanguageOption {
	value: string;
	name: string;
	disabled?: boolean;
}

function Audio({ onClick, audio, language }: AudioProps) {
	if (!audio) {
		return null;
	}
	if (!audio.length || !language || !onClick) {
		return null;
	}

	return (
		<button type="button" title="Play article" onClick={onClick}>
			<span>Play article</span>
			<span>
				<AudioLines size={24} />
			</span>
		</button>
	);
}

export interface AudioProps {
	language?: string;
	audio?: Array<{ id: string; url: string; lang: string }>;
	onClick?: () => void;
}

function SinglePageButton() {
	const { currentArticle, issue, language } = useContext(IssueContext);
	const { setViewMode } = useContext(ReplicaContext);
	const { sendEvent } = useContext(TrackingContext);
	const onClick = () => {
		setViewMode('single');
		sendEvent({
			type: 'BTN_CLICK_REPLICA_SINGLE',
			location: window.location.pathname,
			data: {
				lang: language,
				issue: {
					id: parseInt(`${issue.id}`, 10),
					title: issue.name,
					slug: issue.slug,
				},
				article: {
					id: parseInt(`${currentArticle.id}`, 10),
					ArticleID: parseInt(`${currentArticle.ArticleID}`, 10),
					slug: currentArticle.slug,
					title: currentArticle.name[language],
				},
			},
		});
	};

	return (
		<button type={'button'} title={'Single Page'} onClick={onClick}>
			<span>Single Page View</span>
			<span>
				<Icon name={'single-page-spread'} color={'#000'} />
			</span>
		</button>
	);
}

function DoublePageButton() {
	const { currentArticle, issue, language } = useContext(IssueContext);
	const { setViewMode } = useContext(ReplicaContext);
	const { sendEvent } = useContext(TrackingContext);
	const onClick = () => {
		setViewMode('double');
		sendEvent({
			type: 'BTN_CLICK_REPLICA',
			location: window.location.pathname,
			data: {
				lang: language,
				issue: {
					id: parseInt(`${issue.id}`, 10),
					title: issue.name,
					slug: issue.slug,
				},
				article: {
					id: parseInt(`${currentArticle.id}`, 10),
					ArticleID: parseInt(`${currentArticle.ArticleID}`, 10),
					slug: currentArticle.slug,
					title: currentArticle.name[language],
				},
			},
		});
	};
	return (
		<button type={'button'} title={'Double Page'} onClick={onClick}>
			<span>2-Page View</span>
			<span>
				<Icon name={'double-page-spread'} color={'#000'} />
			</span>
		</button>
	);
}

const saveBlob = (function() {
	const a = document.createElement('a');
	document.body.appendChild(a);
	return function(blob: Blob, fileName: string) {
		const url = window.URL.createObjectURL(blob);
		a.href = url;
		a.download = fileName;
		a.click();
		window.URL.revokeObjectURL(url);
	};
})();
